// Module: practiceDiagramLoader
// -----------------------------------------------------------------
// Receives:  a practice bite's host html url — `practice_host_html` from
//            GET /lessons/{id}/slides, or `html_url` from analytics'
//            /sessions/{id}/slides/{idx}/practice-host
// Produces:  the host export as an iframe srcDoc, as a load state
// Owns:      a per-url cache of in-flight/finished loads, shared by every
//            preview of the same diagram until a caller evicts it
// Does NOT:  render or hydrate math (→ PracticeDiagramFrame), resolve the
//            url (→ callers)
// -----------------------------------------------------------------
import { useEffect, useState } from "react";
import {
  fetchSlideHtml,
  injectIntoHead,
  prefetchSlideDocumentAssets,
  withBaseHref,
} from "@/features/tutor/utils/slideHtmlLoader";
// The app's sanctioned KaTeX stylesheet (see main.jsx), as a string for the
// iframe only — importing it with `?inline` does not apply it to the page.
import etUiCss from "@ever-tutor/et-ui/style.css?inline";

export type PracticeDiagram = { srcDoc: string };

export type PracticeDiagramState =
  | { status: "loading" }
  | { status: "ready"; diagram: PracticeDiagram }
  // No host, or host html with no `.slides section` — a practice authored
  // without a diagram is a supported state, not an error.
  | { status: "missing" }
  | { status: "error" };

const diagramLoadByUrl = new Map<string, Promise<PracticeDiagram | null>>();

let katexStyleTag: string | null = null;

/**
 * The export's formulas are filled in by the parent (PracticeDiagramFrame),
 * so the iframe needs KaTeX's stylesheet. Its font urls are root-relative
 * (`/assets/…`, `/node_modules/…` in dev), and the srcDoc's <base> points at
 * the export's bucket — so they are pinned to the app's own origin. Built
 * once: the stylesheet and the origin never change.
 */
const withKatexStyles = (html: string): string => {
  katexStyleTag ??= `<style>${etUiCss.replace(
    /url\((['"]?)\/(?!\/)/g,
    `url($1${window.location.origin}/`,
  )}</style>`;
  return injectIntoHead(html, katexStyleTag);
};

/**
 * Fetch + build the srcDoc, deduplicated per url. A failed load is evicted so
 * a retry fetches again; a network failure rejects rather than resolving
 * null, so a caller can tell "no diagram" from "couldn't load".
 */
export const loadPracticeDiagram = (
  htmlUrl: string,
): Promise<PracticeDiagram | null> => {
  const cached = diagramLoadByUrl.get(htmlUrl);
  if (cached) return cached;
  const load = fetchSlideHtml(htmlUrl).then((raw) => {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    // Not slide html at all — the same "nothing to show" as a missing host.
    if (doc.querySelector(".slides section") === null) return null;
    prefetchSlideDocumentAssets(doc, htmlUrl);
    return { srcDoc: withKatexStyles(withBaseHref(raw, htmlUrl)) };
  });
  diagramLoadByUrl.set(htmlUrl, load);
  load.catch(() => {
    // Only this load: it may have been evicted and replaced while in flight.
    if (diagramLoadByUrl.get(htmlUrl) === load)
      diagramLoadByUrl.delete(htmlUrl);
  });
  return load;
};

/**
 * Forget these urls so the next load fetches again. Callers evict when they
 * re-read the urls themselves: a host's editor html is overwritten in place on
 * the Azure storage backend (s3 versions the key), so the same url can carry a
 * newer diagram.
 */
export const evictPracticeDiagrams = (htmlUrls: Iterable<string>): void => {
  for (const htmlUrl of htmlUrls) diagramLoadByUrl.delete(htmlUrl);
};

export const resetPracticeDiagramCache = (): void => {
  diagramLoadByUrl.clear();
};

/**
 * Load state for one practice's host diagram. Idle ("loading", no fetch)
 * while `isEnabled` is false, so callers can hold off until the lesson's
 * slide list has arrived.
 *
 * Input:  htmlUrl — host html url, null when the practice has no diagram
 * Output: [state, retry] — retry re-fetches after an "error"
 */
export const usePracticeDiagram = (
  htmlUrl: string | null | undefined,
  isEnabled: boolean,
): [PracticeDiagramState, () => void] => {
  // Keyed by url: when the modal pages from one practice to the next, the
  // render before the effect runs must not show the previous diagram.
  const [loaded, setLoaded] = useState<{
    htmlUrl: string;
    state: PracticeDiagramState;
  } | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!isEnabled || !htmlUrl) return;
    let isCancelled = false;
    setLoaded({ htmlUrl, state: { status: "loading" } });
    loadPracticeDiagram(htmlUrl)
      .then((diagram) => {
        if (isCancelled) return;
        setLoaded({
          htmlUrl,
          state: diagram ? { status: "ready", diagram } : { status: "missing" },
        });
      })
      .catch(() => {
        if (!isCancelled) setLoaded({ htmlUrl, state: { status: "error" } });
      });
    return () => {
      isCancelled = true;
    };
  }, [htmlUrl, isEnabled, retryToken]);

  const retry = () => setRetryToken((token) => token + 1);
  if (!isEnabled) return [{ status: "loading" }, retry];
  if (!htmlUrl) return [{ status: "missing" }, retry];
  if (loaded?.htmlUrl !== htmlUrl) return [{ status: "loading" }, retry];
  return [loaded.state, retry];
};
